import React from 'react';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useOutlet,
  useParams,
  useResolvedPath,
  useRevalidator
} from 'react-router-dom';
import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';

import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ChevronRightIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Spinner } from '@oysterjs/ui/spinner';
import { PageTitle } from '@oysterjs/ui/page';
import { DataTable } from '@oysterjs/ui/data-table';
import {
  CaraWorkspaceArtifactSummaryFieldsFragment,
  CaraWorkspaceArtifactSummaryWithContentFieldsFragment,
  CaraWorkspaceArtifactType,
  CaraWorkspaceState,
  HtmlDocumentTemplateFieldsFragment,
  UpdateCaraWorkspaceArtifactDocument,
  UpdateCaraWorkspaceArtifactsDocument,
  useGetCaraWorkspaceArtifactDesignFitQuery,
  type VisibilityConfigInput
} from '~/api/graphql';
import { Await } from '@oysterjs/ui/async';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import { getStaticsBaseUrl } from '@oysterjs/core/config';
import { OfficeOnlineFilePreview } from '~/components/office-online-embed-url';
import { DocumentTemplateBadge } from '~/components/workspaces/artifact-focus-html-document-template-badge';
import { RenderArtifactButton } from '~/components/workspaces/artifact-focus-html-document-render-button';
import { SaveAsTemplateButton } from '~/components/workspaces/artifact-focus-html-document-save-as-template-button';
import { Editor } from '~/components/document-editor';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { useAutomaticDesignRefit } from '~/components/workspaces/hooks/use-automatic-design-refit';
import { useDocumentDesigns } from '~/components/workspaces/hooks/use-document-designs';
import { documentDesign, savedTemplateDesignId } from '~/components/workspaces/lib/document-design';
import { DesignRefitNotice } from '~/components/workspaces/design-refit-notice';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { useHtmlDocumentExportStyle } from '~/components/workspaces/hooks/use-html-document-export-style';
import { SaveStatusIndicator } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { DocumentVersionBanner } from '~/components/documents/document-version-banner';
import { useArtifactVersionNumber } from '~/components/workspaces/hooks/use-artifact-version-number';
import {
  type DisplayArtifactType,
  buildQueryString,
  documentPreviewPath,
  documentRowPath,
  documentsListPath,
  downloadFile,
  isDocumentArtifact,
  lockedVersion
} from '~/components/documents/documents.utils';
import { defaultDocumentVersion, latestVersion } from '~/components/documents/document-versions';
import {
  type DocumentFilter,
  createDocumentFilterDefinitions,
  filterDocumentArtifacts
} from '~/api/data/documents/documents-filters';
import {
  type DocumentSortKey,
  getDocumentColumns,
  documentSortFn
} from '~/components/documents/documents-columns';
import {
  DocumentArtifactVisibilityControl,
  latestArtifactVersion
} from '~/components/documents/document-artifact-visibility';
import { client } from '~/api/client';
import { ArchivedViewTitle } from '~/components/archived-view-title';
import { FormError } from '~/components/form-error';
import { UPDATE_CARA_WORKSPACE, usePermissions } from '~/components/permissions';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';

const FIT_POLL_MS = 5000;

// Constants
const TYPE_TABS = [
  { type: 'all' as const, label: 'All' },
  { type: CaraWorkspaceArtifactType.TemplateForm as const, label: 'ACORD Forms' },
  { type: CaraWorkspaceArtifactType.PdfFile as const, label: 'Custom PDF Forms' },
  { type: CaraWorkspaceArtifactType.DocumentHtml as const, label: 'Documents' },
  { type: CaraWorkspaceArtifactType.XlsxFile as const, label: 'Excel Files' },
  { type: CaraWorkspaceArtifactType.DocxFile as const, label: 'Word Documents' }
] as const;

const setArtifactsArchived = async (artifactIds: string[], archived: boolean) => {
  await client.mutate({
    mutation: UpdateCaraWorkspaceArtifactsDocument,
    variables: { artifactIds, input: { archived } }
  });
};

const sortVersionsByNumber = (
  versions: CaraWorkspaceArtifactSummaryFieldsFragment['versions']
): CaraWorkspaceArtifactSummaryFieldsFragment['versions'] =>
  [...versions].sort((a, b) => b.version - a.version);

const getArtifactTitle = (artifact: CaraWorkspaceArtifactSummaryFieldsFragment): string =>
  artifact.title;

const getArtifactViewUrl = (
  artifact: CaraWorkspaceArtifactSummaryFieldsFragment,
  version: number,
  options?: { styleId?: string | null }
): string => {
  const baseUrl = getStaticsBaseUrl();
  const workspaceId = artifact.metadata.workspaceId;
  const artifactId = artifact.id;

  const params: Record<string, string | number | null | undefined> = { version };
  if (options?.styleId) {
    params.style_id = options.styleId;
  }

  const queryString = buildQueryString(params);

  if (artifact.type === CaraWorkspaceArtifactType.TemplateForm) {
    return `${baseUrl}/agency/template/${workspaceId}/${artifactId}${queryString}`;
  }
  if (artifact.type === CaraWorkspaceArtifactType.PdfFile) {
    return `${baseUrl}/agency/pdf/${workspaceId}/${artifactId}${queryString}`;
  }
  if (artifact.type === CaraWorkspaceArtifactType.CustomForm) {
    return `${baseUrl}/agency/custom-form/${workspaceId}/${artifactId}${queryString}`;
  }

  if (
    artifact.type === CaraWorkspaceArtifactType.XlsxFile ||
    artifact.type === CaraWorkspaceArtifactType.DocxFile
  ) {
    return `${baseUrl}/agency/application-file/${workspaceId}/${artifactId}?version=${version}`;
  }
  return `${baseUrl}/agency/html/${workspaceId}/${artifactId}/v2${queryString}`;
};

interface ArtifactPreviewPanelProps {
  artifact: CaraWorkspaceArtifactSummaryWithContentFieldsFragment;
  version: number;
  onWorkspaceClick: (workspaceId: string) => void;
  onVersionChange: (version: number) => void;
  /** Where the artifact's latest version opens, for a version that is read-only as older. */
  latestVersionPath: string;
  onDownload?: (artifact: CaraWorkspaceArtifactSummaryFieldsFragment, version: number) => void;
  htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[];
  onConvertToTemplate?: (workspaceId: string, artifactId: string, version: number) => void;
}

export const ArtifactPreviewPanel: React.FC<ArtifactPreviewPanelProps> = ({
  artifact,
  version,
  onWorkspaceClick,
  onVersionChange,
  latestVersionPath,
  onDownload,
  htmlDocumentTemplates = [],
  onConvertToTemplate
}) => {
  const [isIframeLoading, setIsIframeLoading] = React.useState(true);
  const [showPreview, setShowPreview] = React.useState(false);

  const { saveStatus, setSaveStatus, staleVersionMessage, handleUpdate } = useArtifactEditor({
    workspaceId: artifact.metadata.workspaceId,
    artifactId: artifact.id,
    version: version
  });

  const versionLabel = useArtifactVersionNumber();
  const shownVersion = artifact.versions.find((v) => v.version === version);
  const documentForVersion = shownVersion?.document;
  const shownFinal = documentForVersion?.final;
  const { selectedStyleId, setSelectedStyleId, previewStyleId } = useHtmlDocumentExportStyle({
    id: artifact.id,
    version,
    document: documentForVersion
  });

  React.useEffect(() => {
    setIsIframeLoading(true);
  }, [artifact.id, version]);

  const handlePreviewVersionChange = (version: number) => {
    setIsIframeLoading(true);
    onVersionChange(version);
  };

  const latest = latestVersion(artifact.versions);
  const latestNumber = latest?.version;

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
  };

  const pagedDocuments = usePagedDocumentsEnabled();
  const locked = lockedVersion(artifact, version, pagedDocuments);
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CARA_WORKSPACE);
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const designChoices = useDocumentDesigns();
  const templateDesignId = designsOn
    ? savedTemplateDesignId(
        documentDesign(
          { id: artifact.id, version, document: documentForVersion },
          htmlDocumentTemplates
        ),
        !!documentForVersion?.resolvedDesign,
        designChoices
      )
    : undefined;
  const revalidator = useRevalidator();
  const unsaved = saveStatus === 'saving' || saveStatus === 'error';
  // This page has no live feed, so while a fit outlives its answer it asks how the fit ended.
  const [watching, setWatching] = React.useState(false);
  const polled = useGetCaraWorkspaceArtifactDesignFitQuery({
    variables: { workspaceId: artifact.workspace.id, artifactId: artifact.id },
    skip: !watching,
    pollInterval: FIT_POLL_MS,
    // Each answer must be a new read, even when nothing changed.
    fetchPolicy: 'no-cache'
  }).data?.caraWorkspaceArtifact?.versions;
  const fitState = (polled ?? artifact.versions).find(
    (each) => each.version === version
  )?.designFitState;
  const designRefit = useAutomaticDesignRefit({
    artifactId: artifact.id,
    version,
    enabled:
      pagedDocuments &&
      designsOn &&
      canUpdate &&
      !locked &&
      artifact.type === CaraWorkspaceArtifactType.DocumentHtml &&
      // Cara's next write would race a fit started from here.
      artifact.workspace.state !== CaraWorkspaceState.Working &&
      version === latestNumber,
    unsaved,
    fitState,
    onRefitted: (saved) => {
      // The loaded list does not hold the fitted version yet.
      revalidator.revalidate();
      onVersionChange(saved.version);
    }
  });
  const updatingDesign = designRefit.updating;
  React.useEffect(() => setWatching(designRefit.pending), [designRefit.pending]);
  // A fit that outlived its answer wrote a later version, which the loaded list does not hold.
  const polledLatest = polled && Math.max(...polled.map((each) => each.version));
  const followed = React.useRef<number>();
  React.useEffect(() => {
    if (!polledLatest || latestNumber === undefined || polledLatest <= latestNumber) return;
    if (unsaved || followed.current === polledLatest) return;
    followed.current = polledLatest;
    revalidator.revalidate();
    onVersionChange(polledLatest);
  }, [polledLatest, latestNumber, unsaved, revalidator, onVersionChange]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* The actions take a line of their own once the version, template and status no longer
          fit beside them, rather than run over them; the template's name gives way first. */}
      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-4 border-b border-neutral-200 dark:border-neutral-700 relative z-[100] min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-[1_1_18rem]">
          <label className="text-sm text-neutral-600 dark:text-neutral-400 whitespace-nowrap shrink-0">
            Version:
          </label>
          <select
            value={version}
            onChange={(e) => {
              const selectedVersion = artifact.versions.find(
                (v) => v.version === parseInt(e.target.value, 10)
              );
              if (selectedVersion) {
                handlePreviewVersionChange(selectedVersion.version);
              }
            }}
            className="pl-2 pr-8 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 hover:border-neutral-400 dark:hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[60px] shrink-0"
          >
            {sortVersionsByNumber(artifact.versions).map((each) => (
              <option key={each.version} value={each.version}>
                {[
                  versionLabel(each),
                  each.version === latestNumber && 'Latest',
                  each.document?.final && 'Final'
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </option>
            ))}
          </select>
          {artifact.archivedAt && <Badge color="zinc">Archived</Badge>}
          {artifact.type === CaraWorkspaceArtifactType.DocumentHtml &&
            (() => {
              const htmlDocumentTemplateId = artifact.versions
                .find((v) => v.version === version)
                ?.document?.htmlDocumentTemplateId?.trim();
              const template = htmlDocumentTemplateId
                ? htmlDocumentTemplates.find((t) => t.id === htmlDocumentTemplateId)
                : undefined;
              return htmlDocumentTemplateId ? (
                <DocumentTemplateBadge
                  htmlDocumentTemplateId={htmlDocumentTemplateId}
                  templateTitle={template?.title}
                />
              ) : null;
            })()}
          {artifact.type === CaraWorkspaceArtifactType.DocumentHtml && (
            <SaveStatusIndicator status={saveStatus} staleVersionMessage={staleVersionMessage} />
          )}
          {artifact.type === CaraWorkspaceArtifactType.DocumentHtml && locked === 'final' && (
            <span className="truncate text-sm text-neutral-500 dark:text-neutral-400">
              Final · Unmark it in its workspace to edit
            </span>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2 min-w-0 max-w-full">
          {artifact.type === CaraWorkspaceArtifactType.DocumentHtml && (
            <>
              {canUpdate && (
                <SaveAsTemplateButton
                  artifactContent={
                    artifact.versions.find((v) => v.version === version)?.document?.content || ''
                  }
                  artifactTitle={artifact.title}
                  artifactWasConverted={
                    !!artifact.versions.find((v) => v.version === version)?.document?.sourceFileId
                  }
                  designId={templateDesignId}
                  onConvertRequest={
                    onConvertToTemplate
                      ? () => {
                          onConvertToTemplate(artifact.metadata.workspaceId, artifact.id, version);
                        }
                      : undefined
                  }
                />
              )}
              <RenderArtifactButton
                workspaceId={artifact.metadata.workspaceId}
                artifact={{
                  id: artifact.id,
                  version,
                  document: documentForVersion
                }}
                htmlDocumentTemplates={htmlDocumentTemplates}
                selectedStyleId={selectedStyleId}
                onSelectedStyleIdChange={setSelectedStyleId}
                refitDesign={
                  pagedDocuments && canUpdate && !locked && version === latestNumber && !unsaved
                }
                disabled={updatingDesign}
              />
              {(designRefit.failed || designRefit.pending) && (
                <DesignRefitNotice
                  pending={designRefit.pending}
                  onRetry={designRefit.refused ? undefined : designRefit.retry}
                />
              )}
              <DocumentArtifactVisibilityControl artifact={artifact} />
            </>
          )}
          {onDownload &&
            (artifact.type === CaraWorkspaceArtifactType.XlsxFile ||
              artifact.type === CaraWorkspaceArtifactType.DocxFile) && (
              <>
                <button
                  type="button"
                  onClick={() => onDownload(artifact, version)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 rounded transition-colors"
                >
                  <span>Download</span>
                </button>
                <DocumentArtifactVisibilityControl artifact={artifact} versionNumber={version} />
              </>
            )}
          {artifact.type !== CaraWorkspaceArtifactType.DocumentHtml &&
            artifact.type !== CaraWorkspaceArtifactType.XlsxFile &&
            artifact.type !== CaraWorkspaceArtifactType.DocxFile && (
              <DocumentArtifactVisibilityControl artifact={artifact} versionNumber={version} />
            )}
          <button
            type="button"
            onClick={() => onWorkspaceClick(artifact.metadata.workspaceId)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 rounded transition-colors"
          >
            <span>Go to workspace</span>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {latest && version < latest.version && (
        <DocumentVersionBanner
          className="mt-3 flex-shrink-0"
          shown={{
            label: shownVersion ? versionLabel(shownVersion) : String(version),
            final: !!shownFinal
          }}
          latest={{ label: versionLabel(latest), final: !!latest.document?.final }}
          openLatest={latestVersionPath}
        />
      )}
      <div className="relative flex-1 min-h-0 pt-4 flex flex-col overflow-hidden">
        <div className="relative flex-1 min-h-0 w-full flex flex-col overflow-hidden">
          {artifact.type === CaraWorkspaceArtifactType.XlsxFile ? (
            <OfficeOnlineFilePreview
              downloadUrl={getArtifactViewUrl(artifact, version)}
              iframeTitle="Excel workbook preview"
            />
          ) : artifact.type === CaraWorkspaceArtifactType.DocxFile ? (
            <OfficeOnlineFilePreview
              downloadUrl={getArtifactViewUrl(artifact, version)}
              iframeTitle="Word document preview"
            />
          ) : artifact.type === CaraWorkspaceArtifactType.DocumentHtml ? (
            <Editor
              key={`${artifact.id}-${version}`}
              content={
                artifact.versions.find((v) => v.version === version)?.document?.content || ''
              }
              editable={!locked && !updatingDesign}
              readOnlyNote={updatingDesign ? 'Updating to your Design…' : undefined}
              onUpdate={handleUpdate}
              onSaveStatusChange={setSaveStatus}
              workspaceId={artifact.metadata.workspaceId}
              templateId={
                artifact.type === CaraWorkspaceArtifactType.DocumentHtml
                  ? previewStyleId
                  : artifact.metadata.templateId
                    ? String(artifact.metadata.templateId)
                    : undefined
              }
              artifactId={artifact.id}
              artifactVersion={version}
              artifactRevision={
                designRefit.renderRevision
                  ? `${artifact.versions.find((v) => v.version === version)?.revision}:${designRefit.renderRevision}`
                  : artifact.versions.find((v) => v.version === version)?.revision
              }
              showPreview={showPreview}
              onPreviewToggle={() => setShowPreview(!showPreview)}
            />
          ) : (
            <>
              {isIframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-50 dark:bg-neutral-800 rounded-lg z-10">
                  <div className="flex flex-col items-center gap-3">
                    <Spinner size="size-8" color="neutral" />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Loading preview...
                    </p>
                  </div>
                </div>
              )}
              <iframe
                key={`${artifact.id}-${version}`}
                src={getArtifactViewUrl(artifact, version)}
                className="w-full h-full border-0 rounded-lg"
                title="Artifact Preview"
                onLoad={handleIframeLoad}
                style={{ opacity: isIframeLoading ? 0 : 1 }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const DocumentPage = ({ archived = false }: { archived?: boolean } = {}) => {
  const outlet = useOutlet();
  const revalidator = useRevalidator();
  const visibilityEnabled = useVisibilityEntitlement();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CARA_WORKSPACE);
  const loader = useLoaderData() as
    | {
        data: Promise<{
          caraWorkspaceArtifacts?: CaraWorkspaceArtifactSummaryFieldsFragment[];
        }>;
      }
    | undefined;

  const [selectedType, setSelectedType] = React.useState<DisplayArtifactType | 'all'>('all');
  const [selectedArtifactIds, setSelectedArtifactIds] = React.useState<string[]>([]);
  const selectedSet = React.useMemo(() => new Set(selectedArtifactIds), [selectedArtifactIds]);
  const [showVisibilityDialog, setShowVisibilityDialog] = React.useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = React.useState(false);
  const [bulkVisibilityMessage, setBulkVisibilityMessage] = React.useState<React.ReactNode>(null);
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const filterDefinitions = React.useMemo(() => createDocumentFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<DocumentFilter, DocumentSortKey>(
      archived ? 'cara_documents_archived_view' : 'cara_documents_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  React.useEffect(() => {
    setSelectedArtifactIds([]);
    setBulkVisibilityMessage(null);
    setArchiveError(null);
  }, [selectedType, archived]);

  // All-or-nothing on the server, so there is no partial result to report.
  const archiveSelected = React.useCallback(async () => {
    setIsArchiving(true);
    setArchiveError(null);
    try {
      await setArtifactsArchived(selectedArtifactIds, !archived);
      setShowArchiveDialog(false);
      setSelectedArtifactIds([]);
      revalidator.revalidate();
    } catch {
      setArchiveError(
        archived
          ? 'The selected documents could not be restored. Please try again.'
          : 'The selected documents could not be archived. Please try again.'
      );
    } finally {
      setIsArchiving(false);
    }
  }, [archived, revalidator, selectedArtifactIds]);

  const updateArtifactVisibility = React.useCallback(
    async (
      visibility: VisibilityConfigInput,
      artifacts: CaraWorkspaceArtifactSummaryFieldsFragment[]
    ) => {
      const selectedArtifacts = artifacts.filter((artifact) => selectedSet.has(artifact.id));

      setIsUpdatingVisibility(true);
      setBulkVisibilityMessage(null);
      try {
        const results = await Promise.all(
          selectedArtifacts.map(async (artifact) => {
            const latest = latestArtifactVersion(artifact);
            if (!latest) {
              return { id: artifact.id, ok: false };
            }
            try {
              await client.mutate({
                mutation: UpdateCaraWorkspaceArtifactDocument,
                variables: {
                  workspaceId: artifact.metadata.workspaceId,
                  artifactId: artifact.id,
                  version: latest.version,
                  input: { visibility }
                }
              });
              return { id: artifact.id, ok: true };
            } catch {
              return { id: artifact.id, ok: false };
            }
          })
        );
        const failedIds = results.filter((result) => !result.ok).map((result) => result.id);
        const updatedCount = results.length - failedIds.length;
        revalidator.revalidate();
        if (failedIds.length > 0) {
          setSelectedArtifactIds(failedIds);
          setBulkVisibilityMessage(
            `${updatedCount} updated, ${failedIds.length} failed. Failed documents remain selected.`
          );
          return;
        }
        setShowVisibilityDialog(false);
        setSelectedArtifactIds([]);
      } finally {
        setIsUpdatingVisibility(false);
      }
    },
    [revalidator, selectedSet]
  );

  return (
    <PageTitle
      title={
        <ArchivedViewTitle
          archived={archived}
          title="Documents"
          archivedTitle="Archived documents"
          basePath={documentsListPath(false)}
          noun="documents"
        />
      }
      description={
        archived
          ? 'Documents archived out of the Documents list. They stay in their workspaces, and any can be restored.'
          : 'Documents, PDFs, and forms generated by Cara'
      }
    >
      <div className="-mt-2">
        {/* Type Toggle Navigation */}
        <div className="hidden sm:block">
          <div className="border-b border-neutral-200 dark:border-neutral-700">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.type}
                  onClick={() => setSelectedType(tab.type)}
                  className={clsx(
                    selectedType === tab.type
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:border-neutral-200 dark:hover:border-white/10 hover:text-neutral-700 dark:hover:text-neutral-300',
                    'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
        <div className="sm:hidden">
          <label htmlFor="document-type-tabs" className="sr-only">
            Select a document type
          </label>
          <select
            id="document-type-tabs"
            name="document-type-tabs"
            value={selectedType}
            onChange={(e) => setSelectedType(e.currentTarget.value as DisplayArtifactType | 'all')}
            className="block w-full rounded-md border-neutral-300 dark:border-neutral-600 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
          >
            {TYPE_TABS.map((tab) => (
              <option key={tab.type} value={tab.type}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        <SearchFilterInput
          filterDefinitions={filterDefinitions}
          onFiltersChange={setActiveFilters}
          initialFilters={initialFilters}
          placeholder="Filter by user, date, or search..."
        />

        <React.Suspense fallback={<DocumentPageLoader />}>
          <Await resolve={loader?.data}>
            {({ caraWorkspaceArtifacts }) => {
              const all = (caraWorkspaceArtifacts || []).filter((a) => isDocumentArtifact(a.type));
              const typeFiltered =
                selectedType === 'all'
                  ? all
                  : selectedType === CaraWorkspaceArtifactType.PdfFile
                    ? all.filter(
                        (a) =>
                          a.type === CaraWorkspaceArtifactType.PdfFile ||
                          a.type === CaraWorkspaceArtifactType.CustomForm
                      )
                    : all.filter((a) => a.type === selectedType);
              const filtered = filterDocumentArtifacts(typeFiltered, activeFilters);
              const columns = getDocumentColumns({
                selectedType,
                selectedSet,
                setSelectedArtifacts: setSelectedArtifactIds,
                filtered
              });

              return (
                <div className="relative">
                  <FloatingSelectionBar
                    count={selectedArtifactIds.length}
                    onClear={() => {
                      setBulkVisibilityMessage(null);
                      setArchiveError(null);
                      setSelectedArtifactIds([]);
                    }}
                    visibilityEnabled={visibilityEnabled && !archived}
                    isUpdatingVisibility={isUpdatingVisibility}
                    onVisibility={() => {
                      setBulkVisibilityMessage(null);
                      setShowVisibilityDialog(true);
                    }}
                    archiveEnabled={canUpdate}
                    isArchiving={isArchiving}
                    archiveLabel={archived ? 'Restore' : 'Archive'}
                    archiveIcon={archived ? <ArrowUturnLeftIcon className="size-3.5" /> : undefined}
                    onArchive={() => {
                      setArchiveError(null);
                      setShowArchiveDialog(true);
                    }}
                  />
                  <FormError
                    message={archiveError}
                    className="mb-3 text-sm text-red-600 dark:text-red-400"
                  />
                  {filtered.length === 0 ? (
                    <LibraryListEmptyState
                      icon={archived ? ArchiveBoxIcon : FolderIcon}
                      title={archived ? 'No archived documents' : 'No documents found'}
                      description={
                        activeFilters.length > 0
                          ? 'Try adjusting your search or filters.'
                          : archived
                            ? 'Documents you archive will show up here.'
                            : 'No documents available.'
                      }
                    />
                  ) : (
                    <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                      <DataTable
                        data={filtered}
                        columns={columns}
                        keyExtractor={(a) => a.id}
                        rowHref={(a) => documentRowPath(documentsListPath(archived), a)}
                        tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                        sortKey={sortBy}
                        sortDirection={sortDirection}
                        onSort={setSort}
                        sortFn={documentSortFn}
                      />
                    </div>
                  )}
                  <BulkVisibilityDialog
                    open={showVisibilityDialog}
                    count={selectedArtifactIds.length}
                    itemLabel="document"
                    notice="This changes only the selected documents. Their workspaces keep their current visibility."
                    resultMessage={bulkVisibilityMessage}
                    loading={isUpdatingVisibility}
                    onClose={() => {
                      setBulkVisibilityMessage(null);
                      setShowVisibilityDialog(false);
                    }}
                    onSave={(visibility) => updateArtifactVisibility(visibility, filtered)}
                  />
                  <ArchiveConfirmDialog
                    open={showArchiveDialog}
                    count={selectedArtifactIds.length}
                    itemNoun="document"
                    title={archived ? 'Restore documents' : undefined}
                    confirmLabel={archived ? 'Restore' : undefined}
                    description={
                      archived ? (
                        <>
                          Restore <strong>{selectedArtifactIds.length}</strong>{' '}
                          {selectedArtifactIds.length === 1 ? 'document' : 'documents'}? They'll
                          move back to your documents.
                        </>
                      ) : (
                        <>
                          Are you sure you want to archive{' '}
                          <strong>{selectedArtifactIds.length}</strong>{' '}
                          {selectedArtifactIds.length === 1 ? 'document' : 'documents'}? They will
                          leave this list but stay in their workspaces, and you can restore them
                          later from the archived documents view.
                        </>
                      )
                    }
                    loading={isArchiving}
                    onCancel={() => setShowArchiveDialog(false)}
                    onConfirm={archiveSelected}
                  />
                </div>
              );
            }}
          </Await>
        </React.Suspense>
      </div>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, {})}
      </AnimatePresence>
    </PageTitle>
  );
};

export const DocumentPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading documents...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

/** Builds its paths from the parent route, so it works under any list. */
export const DocumentPreviewPage = () => {
  const params = useParams<{ workspaceId: string; artifactId: string; version: string }>();
  const listPath = useResolvedPath('..').pathname;
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          caraWorkspaceArtifact?: CaraWorkspaceArtifactSummaryWithContentFieldsFragment;
          htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[];
        }>;
      }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate(listPath);
    } else {
      navigate(-1);
    }
  };

  const handleWorkspaceClick = (workspaceId: string) => {
    navigate(`/app/workspaces/${workspaceId}`);
  };

  const handleConvertToTemplate = (workspaceId: string, artifactId: string, version: number) => {
    navigate(
      `/app/workspaces/${workspaceId}?convert_to_template=true&artifact_id=${artifactId}&version=${version}`
    );
  };

  const handleDownloadSingle = async (
    artifact: CaraWorkspaceArtifactSummaryFieldsFragment,
    version: number
  ) => {
    const downloadUrl = getArtifactViewUrl(artifact, version);
    const title = getArtifactTitle(artifact);
    const fileExtension =
      artifact.type === CaraWorkspaceArtifactType.DocumentHtml
        ? 'html'
        : artifact.type === CaraWorkspaceArtifactType.XlsxFile
          ? 'xlsx'
          : artifact.type === CaraWorkspaceArtifactType.DocxFile
            ? 'docx'
            : 'pdf';
    await downloadFile(downloadUrl, `${title}.${fileExtension}`);
  };

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <div className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white dark:bg-neutral-800 p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </div>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.caraWorkspaceArtifact || !params.workspaceId || !params.artifactId) {
            return null;
          }

          const version = params.version
            ? parseInt(params.version, 10)
            : defaultDocumentVersion(data.caraWorkspaceArtifact.versions)?.version || 1;

          return (
            <MountableSlideout
              onClose={onClose}
              title={data.caraWorkspaceArtifact.title}
              size="4xl"
            >
              <ArtifactPreviewPanel
                key={`${data.caraWorkspaceArtifact.id}-${version}`}
                artifact={data.caraWorkspaceArtifact}
                version={version}
                onWorkspaceClick={handleWorkspaceClick}
                onVersionChange={(version) => {
                  navigate(
                    documentPreviewPath(
                      listPath,
                      data.caraWorkspaceArtifact?.metadata.workspaceId ?? '',
                      data.caraWorkspaceArtifact?.id ?? '',
                      version
                    ),
                    { replace: true }
                  );
                }}
                latestVersionPath={documentPreviewPath(
                  listPath,
                  data.caraWorkspaceArtifact.metadata.workspaceId,
                  data.caraWorkspaceArtifact.id,
                  Math.max(...data.caraWorkspaceArtifact.versions.map((each) => each.version))
                )}
                onDownload={handleDownloadSingle}
                htmlDocumentTemplates={data?.htmlDocumentTemplates || []}
                onConvertToTemplate={handleConvertToTemplate}
              />
            </MountableSlideout>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
